/**
 * usePortfolioScope — the operator's remembered "whose book am I looking at"
 * scope for the Operator (Portfolio) switcher (TRACK D · F4.4 / D5.4).
 *
 * The requirement is "each logged-in employee sees their book of products by
 * default", which a per-BROWSER store can't satisfy (a shared machine, or the
 * same employee on a second device, would restore the wrong book). So the
 * selection is SERVER-persisted per operator (GET/PUT /api/operator-prefs,
 * backed by bbpro_operator_prefs / migration 089); this hook keeps a
 * localStorage MIRROR purely as a first-paint cache so the switcher renders the
 * last-known scope with no network flash, then reconciles against the server row
 * once it lands.
 *
 * The scope is one of:
 *   - 'mine'           — my owned products (the F4.2 ownership map intersected
 *                        with the request operator, resolved by the later F4.6
 *                        scoping phase),
 *   - 'all'            — the whole catalog (the default, == today's behavior),
 *   - '<operator-slug>'— view another employee's book.
 *
 * Persistence + sync, mirroring the `ProfileSwitcher` / `useMarketplaceScope`
 * idioms already in this codebase:
 *   - localStorage `adbetter.portfolioScope` is the fast first-paint cache,
 *   - writes go through to the server (best-effort) AND the cache,
 *   - cross-tab refresh reuses the same `storage` event + the existing
 *     `adbetter.profiles.changed` CustomEvent broadcast (so the header switcher,
 *     wherever it lives, and any consumer stay in lockstep without a second bus).
 *
 * NULL-tolerant end to end: a stale/unknown scope coerces to 'all', a server
 * fetch failure leaves the cached value in place, and an unidentified caller
 * (no resolvable operator) simply stays whole-catalog — never an error.
 */
import { useCallback, useEffect, useState } from 'react'
import { api, type OperatorPrefs } from './api'

/** A portfolio scope token. 'mine' / 'all' are synthetic; any other string is a
 *  concrete operator slug (view that employee's book). */
export type PortfolioScope = string

/** The neutral default — whole catalog, == the app's behavior before F4. */
export const DEFAULT_PORTFOLIO_SCOPE: PortfolioScope = 'all'

const STORAGE_KEY = 'adbetter.portfolioScope'
//: The same broadcast channel ProfileSwitcher uses, so same-tab listeners
//: (the native `storage` event only fires in OTHER tabs) refresh together.
const STORAGE_EVENT = 'adbetter.profiles.changed'

/** Pure: coerce any stored/foreign value to a usable scope, falling back to
 *  'all'. A blank/whitespace value is the default; everything else (a synthetic
 *  token OR an operator slug) is lower-cased and kept — an unresolvable slug is
 *  resolved to its meaning by the consuming UI, not rejected here. Exposed (and
 *  trivially testable) so the rule lives in one place. */
export function coercePortfolioScope(raw: string | null | undefined): PortfolioScope {
  const s = (raw ?? '').trim().toLowerCase()
  return s || DEFAULT_PORTFOLIO_SCOPE
}

function readCached(): PortfolioScope {
  if (typeof window === 'undefined') return DEFAULT_PORTFOLIO_SCOPE
  try {
    return coercePortfolioScope(window.localStorage.getItem(STORAGE_KEY))
  } catch {
    return DEFAULT_PORTFOLIO_SCOPE
  }
}

function writeCached(scope: PortfolioScope) {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(STORAGE_KEY, scope)
  } catch {
    /* private mode / quota — the choice still applies for this tab + persists
       server-side; only the cross-tab fast-cache mirror is skipped */
  }
  // Notify same-tab listeners (the native `storage` event only fires in other
  // tabs/windows). Consumers subscribe to both, like useActiveProfiles.
  window.dispatchEvent(new CustomEvent(STORAGE_EVENT))
}

/**
 * The operator's portfolio scope. Returns the current scope, a setter, and the
 * loading/hydration flags so a switcher can show "syncing" on first paint:
 *
 *   - `scope`     — the active scope (cache-seeded, server-reconciled),
 *   - `setScope`  — persist a new scope (server PUT best-effort + cache + broadcast),
 *   - `hydrated`  — true once the server row has been reconciled at least once,
 *   - `persisted` — true when the active scope is an explicit saved choice (vs
 *                   the implicit whole-catalog default the operator never set).
 *
 * Seeds synchronously from localStorage (no flash), then fetches the server
 * preference on mount and adopts it if it differs. Cross-tab changes flow back
 * in live via the storage / `adbetter.profiles.changed` events.
 */
export function usePortfolioScope(): {
  scope: PortfolioScope
  setScope: (scope: PortfolioScope) => void
  hydrated: boolean
  persisted: boolean
} {
  const [scope, setScopeState] = useState<PortfolioScope>(readCached)
  const [hydrated, setHydrated] = useState(false)
  const [persisted, setPersisted] = useState(false)

  // Reconcile against the server row once on mount. The server is the source of
  // truth (per-operator, cross-device); the cache only avoids a first-paint
  // flash. A failed fetch leaves the cached value in place (NULL-tolerant).
  useEffect(() => {
    let alive = true
    api.getOperatorPrefs()
      .then((prefs: OperatorPrefs) => {
        if (!alive) return
        const next = coercePortfolioScope(prefs.selected_scope)
        setScopeState(next)
        setPersisted(prefs.persisted)
        writeCached(next)
      })
      .catch(() => { /* offline / unidentified — keep the cached scope */ })
      .finally(() => { if (alive) setHydrated(true) })
    return () => { alive = false }
  }, [])

  // Cross-tab sync: adopt scope changes made in OTHER tabs (native `storage`)
  // and SAME-tab broadcasts (the CustomEvent) so every consumer stays aligned.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const refresh = (e?: Event) => {
      if (e instanceof StorageEvent && e.key !== STORAGE_KEY) return
      setScopeState(readCached())
    }
    window.addEventListener('storage', refresh)
    window.addEventListener(STORAGE_EVENT, refresh as EventListener)
    return () => {
      window.removeEventListener('storage', refresh)
      window.removeEventListener(STORAGE_EVENT, refresh as EventListener)
    }
  }, [])

  const setScope = useCallback((next: PortfolioScope) => {
    const coerced = coercePortfolioScope(next)
    // Optimistic: update + cache + broadcast immediately for a snappy switch,
    // then persist to the server. A failed PUT doesn't roll back (the choice is
    // a low-stakes preference; it'll re-sync from the server on next mount).
    setScopeState(coerced)
    setPersisted(true)
    writeCached(coerced)
    api.putOperatorPrefs({ selected_scope: coerced }).catch(() => {
      /* unidentified / offline — the cache holds the choice for this session */
    })
  }, [])

  return { scope, setScope, hydrated, persisted }
}
